import { ButtonStyle } from './../../../../system/modal-confirm/modal-confirm.component';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem
} from '@angular/cdk/drag-drop';
import {
  ConditionItem,
  ConditionSource,
  ConditionTarget
} from 'src/app/components/system/condition-builder/condition-builder.model';
import {
  Activity,
  ActivityModel,
  DataEntityLayoutModel,
  DataEntityTableLayoutModel,
  DataEntityGroupLayoutModel,
  DataEntityRowLayoutModel,
  EntityTableCelllayout,
  EntityTableRowLayout
} from 'src/app/models/activities';
import { DataEntity } from 'src/app/models/data-entities';
import { DataEntityEditorComponent } from '../../data-entity-editor/data-entity-editor.component';
import { Workflow } from 'src/app/models/workflow';
import { WorkflowContextService, WorkflowService } from 'src/app/services';

@Component({
  selector: 'wm-data-entity-table-layout-editor',
  templateUrl: './data-entity-table-layout-editor.component.html',
  styleUrl: './data-entity-table-layout-editor.component.css',
  standalone: false,
})
export class DataEntityTableLayoutEditorComponent implements OnInit {
  configureForm: UntypedFormGroup;
  configureLabel: string = 'Configure Table';
  ButtonStyle = ButtonStyle;
  @Input() layout: DataEntityTableLayoutModel;
  @Input() workflow: Workflow;

  @Input() activity: Activity<ActivityModel>;
  @Input() form: UntypedFormGroup;
  @ViewChild('newDEEditor', { static: false })
  newDEEditor: DataEntityEditorComponent;
  newDEForm: UntypedFormGroup;
  showEditor: boolean;
  // Emit changes whenever layout updates
  @Output() layoutChanged = new EventEmitter<EntityTableRowLayout[]>();
  currentRow: EntityTableRowLayout = null;
  currentCell: EntityTableCelllayout = null;

  // Conditional visibility state
  showConditionalVisible = false;
  conditionSources: ConditionSource[];
  // Tracks which rows have their conditional-visibility UI expanded (keyed by row reference).
  private _rowShowConditionalVisible = new WeakMap<EntityTableRowLayout, boolean>();

  ngOnInit() {
    // Initialize the form and any other necessary data
    this.newDEForm = new UntypedFormGroup({});

    // Initialize colspan and rowSpan for existing cells
    if (this.layout && this.layout.rows) {
      this.layout.rows.forEach(row => {
        if (row.cells) {
          row.cells.forEach(cell => {
            if(!cell.type) {
              if(cell.staticText) {
                cell.type = 'text';
              }
              else {
                cell.type = 'entity';
              }
            }
            if (cell.colspan === undefined || cell.colspan === null) {
              cell.colspan = 1;
            }
            if (cell.rowSpan === undefined || cell.rowSpan === null) {
              cell.rowSpan = 1;
            }
          });
        }
      });
    }

    // Initialize table-level conditional-visibility toggle from any existing criteria.
    this.showConditionalVisible = this.hasTableConditionalCriteria();

    // Load condition sources (data entities available for building criteria).
    if (this.activity) {
      this._workflowSvc
        .getDataEntitiesBeforeMe(this.workflow || this._context.workflow, this.activity)
        .subscribe(entities => {
          this.conditionSources = entities.map(e => {
            return {
              name: e.templateCode,
              value: e.templateCode,
              operators: e.getAvailableOperators(),
              possibleValues: e.getPossibleValues()
            };
          });
        });
    }
  }

  // ==== Table-level conditional visibility ====

  private hasTableConditionalCriteria(): boolean {
    return !!(
      this.layout?.visibleCriteria &&
      this.layout.visibleCriteria.group &&
      this.layout.visibleCriteria.group.conditions?.length > 0
    );
  }

  visibleConditionChanged(): void {
    if (this.showConditionalVisible) {
      if (this.layout.visibleCriteria == null) {
        this.layout.visibleCriteria = new ConditionTarget({
          group: new ConditionItem({ isGroup: true }),
          value: 'Yes'
        });
      }
    } else {
      // Toggle off: clear the criteria so evaluation returns "visible".
      this.layout.visibleCriteria = null;
    }

    this.emitChanges();

    this._ref.detectChanges();
  }

  visibleCriteriaChanged(group: any): void {
    if (this.layout?.visibleCriteria) {
      this.layout.visibleCriteria.group = group;
    }

    this.emitChanges();
  }

  // ==== Row-level conditional visibility ====

  rowShowConditionalVisible(row: EntityTableRowLayout): boolean {
    if (!row) {
      return false;
    }

    if (this._rowShowConditionalVisible.has(row)) {
      return this._rowShowConditionalVisible.get(row)!;
    }

    // Default from existing data.
    const has = !!(
      row.visibleCriteria &&
      row.visibleCriteria.group &&
      row.visibleCriteria.group.conditions?.length > 0
    );

    this._rowShowConditionalVisible.set(row, has);

    return has;
  }

  setRowShowConditionalVisible(row: EntityTableRowLayout, value: boolean): void {
    if (!row) {
      return;
    }

    this._rowShowConditionalVisible.set(row, value);

    if (value) {
      if (row.visibleCriteria == null) {
        row.visibleCriteria = new ConditionTarget({
          group: new ConditionItem({ isGroup: true }),
          value: 'Yes'
        });
      }
    } else {
      row.visibleCriteria = null;
    }

    this.emitChanges();

    this._ref.detectChanges();
  }

  rowVisibleCriteriaChanged(row: EntityTableRowLayout, group: any): void {
    if (row?.visibleCriteria) {
      row.visibleCriteria.group = group;
    }
    
    this.emitChanges();
  }

  // Current table layout (array of rows)
  // @Input() tableRows: EntityTableRowLayout[] = [];


  constructor(
    public _workflowSvc: WorkflowService,
    private _context: WorkflowContextService,
    private _ref: ChangeDetectorRef
  ) {}


  addRow() {
    this.currentRow = { cells: [] };
    this.layout.rows = this.layout.rows || [];
    this.layout.rows.push(this.currentRow);
    this.emitChanges();
  }

  removeRow(rowIndex: number) {
    this.currentRow = null;
    this.currentCell = null;
    this.layout.rows.splice(rowIndex, 1);
    this.emitChanges();
  }

  addCell(row: EntityTableRowLayout, type: 'text' | 'entity' | 'layout' = 'text') {
    this.currentRow = row;
    this.currentCell = {
      type: type,
      staticText: null,
      entity: null,
      layoutModel: null,
      colspan: 1,
      rowSpan: 1
    };

    row.cells.push(this.currentCell);
    this.emitChanges();
  }

  removeCell(row: EntityTableRowLayout, cellIndex: number) {
    this.currentCell = null;
    row.cells.splice(cellIndex, 1);
    this.emitChanges();
  }

  // True if a cell (or anything inside its nested layoutModel) contains
  // an entity with isSystemEntity = true.
  cellHasSystemEntity(cell: EntityTableCelllayout): boolean {
    if (!cell) {
      return false;
    }
    if (cell.entity && (cell.entity as any).isSystemEntity) {
      return true;
    }
    if (cell.layoutModel?.getDataEntities) {
      return (cell.layoutModel.getDataEntities() || []).some(
        e => e && (e as any).isSystemEntity
      );
    }
    return false;
  }

  // True if any cell in the row (including nested layouts) contains a system entity.
  rowHasSystemEntity(row: EntityTableRowLayout): boolean {
    return !!row?.cells?.some(c => this.cellHasSystemEntity(c));
  }

  viewDataEntity(
    row: EntityTableRowLayout,
    cell: EntityTableCelllayout,
    entity: DataEntity
  ) {
    cell.showEditor = true;
    this._ref.detectChanges();
  }

  saveDataEntity(cell: EntityTableCelllayout, entity: DataEntity) {
    cell.entity = entity;
    cell.showEditor = false;
    this.emitChanges();
  }

  removeDataEntity(cell: EntityTableCelllayout) {
    cell.entity = null;
    this.emitChanges();
  }

  // Collect DataEntities currently placed in any cell of this table layout,
  // including entities nested inside any layoutModel on a cell.
  private getEntitiesInThisLayout(): DataEntity[] {
    const entities: DataEntity[] = [];
    if (this.layout?.rows) {
      this.layout.rows.forEach(row => {
        if (row?.cells) {
          row.cells.forEach(cell => {
            if (cell.entity) {
              entities.push(cell.entity);
            }
            if (cell.layoutModel?.getDataEntities) {
              const nested = cell.layoutModel.getDataEntities() || [];
              nested.forEach(e => {
                if (e) {
                  entities.push(e);
                }
              });
            }
          });
        }
      });
    }
    return entities;
  }

  // Get the activity's default-layout column entity wrapper arrays (single- and two-column variants)
  private getActivityDefaultColumnGroups(): DataEntityLayoutModel[][] {
    const formLayoutModel: any = (this.activity?.model as any)?.formLayoutModel;
    if (!formLayoutModel) {
      return [];
    }
    return [
      formLayoutModel.columnEntities,
      formLayoutModel.leftColumnEntities,
      formLayoutModel.rightColumnEntities
    ].filter((c: any): c is DataEntityLayoutModel[] => Array.isArray(c));
  }

  // List of DataEntities available on the activity (as default column entities)
  // that are not already used in this table layout.
  getAvailableActivityEntities(): DataEntity[] {
    const inLayout = this.getEntitiesInThisLayout();
    const result: DataEntity[] = [];

    this.getActivityDefaultColumnGroups().forEach(group => {
      group.forEach(wrapper => {
        const isDefault =
          !wrapper.type || wrapper.type === 'default-data-entity-layout';
        if (!isDefault || !wrapper.entity) {
          return;
        }
        const alreadyInLayout = inLayout.some(
          e =>
            e === wrapper.entity ||
            (!!e.templateCode &&
              !!wrapper.entity.templateCode &&
              e.templateCode === wrapper.entity.templateCode)
        );
        if (!alreadyInLayout) {
          result.push(wrapper.entity);
        }
      });
    });

    return result;
  }

  // Move an existing DataEntity from the activity's default column position
  // into the given entity cell of this table layout.
  addExistingEntity(cell: EntityTableCelllayout, entity: DataEntity) {
    if (!cell || !entity) {
      return;
    }

    const groups = this.getActivityDefaultColumnGroups();
    for (const group of groups) {
      const idx = group.findIndex(
        w =>
          w.entity === entity ||
          (!!w.entity?.templateCode &&
            !!entity.templateCode &&
            w.entity.templateCode === entity.templateCode)
      );
      if (idx > -1) {
        group.splice(idx, 1);
        break;
      }
    }

    cell.entity = entity;
    this.emitChanges();
  }

  // Reverse of addExistingEntity: clear the entity from the cell and put it back
  // as a default-data-entity-layout wrapper in the activity's column entities.
  moveEntityBackToActivity(cell: EntityTableCelllayout) {
    if (!cell?.entity) {
      return;
    }

    const entity = cell.entity;
    const groups = this.getActivityDefaultColumnGroups();
    const targetGroup = groups[0];
    if (targetGroup) {
      const alreadyPresent = targetGroup.some(
        w =>
          w.entity === entity ||
          (!!w.entity?.templateCode &&
            !!entity.templateCode &&
            w.entity.templateCode === entity.templateCode)
      );
      if (!alreadyPresent) {
        targetGroup.push(
          new DataEntityLayoutModel({
            entity,
            displayOrder: targetGroup.length
          })
        );
      }
    }

    cell.entity = null;
    this.emitChanges();
  }

  trackAvailableEntity(_index: number, entity: DataEntity): string {
    return entity?.templateCode || String(_index);
  }

  // Layout model management
  addLayoutModel(cell: EntityTableCelllayout, layoutType: string) {
    switch (layoutType) {
      case 'table-data-entity-layout':
        cell.layoutModel = new DataEntityTableLayoutModel();
        break;
      case 'group-data-entity-layout':
        cell.layoutModel = new DataEntityGroupLayoutModel();
        break;
      case 'row-data-entity-layout':
        cell.layoutModel = new DataEntityRowLayoutModel();
        break;
      case 'default-data-entity-layout':
        cell.layoutModel = new DataEntityLayoutModel({ entity: null });
        break;
    }
    this.emitChanges();
  }

  removeLayoutModel(cell: EntityTableCelllayout) {
    cell.layoutModel = null;
    this.emitChanges();
  }

  onLayoutChanged(cell: EntityTableCelllayout, layout: any) {
    this.emitChanges();
  }

  trackRow(index: number, _row: EntityTableRowLayout) {
    return index;
  }
  trackCell(index: number, _cell: EntityTableCelllayout) {
    return index;
  }

  onCellUpdated() {
    this.emitChanges();
  }

  private emitChanges() {
    this.layoutChanged.emit(this.layout.rows);
  }

  saveNew(cell: EntityTableCelllayout) {
    this.layout.rows = this.layout.rows || [];
    cell.entity = this.newDEEditor.dataEntity;
    cell.showEditor = true;
  }
  cancelNew(cell: EntityTableCelllayout) {
    cell.showEditor = false;
  }
  loadEditor(cell: EntityTableCelllayout) {
    cell.showEditor = true;
  }

  // Drag and drop functionality for swapping entities between cells
  drop(event: CdkDragDrop<any>) {
    const sourceCell = event.previousContainer.data as EntityTableCelllayout;
    const targetCell = event.container.data as EntityTableCelllayout;

    if (sourceCell && targetCell && sourceCell !== targetCell) {
      // Swap entities between cells
      this.swapCellEntities(sourceCell, targetCell);
    }
  }

  private swapCellEntities(
    sourceCell: EntityTableCelllayout,
    targetCell: EntityTableCelllayout
  ) {
    // Store the source entity temporarily
    const tempEntity = sourceCell.entity;

    // Move target entity to source cell (can be null)
    sourceCell.entity = targetCell.entity;

    // Move source entity to target cell
    targetCell.entity = tempEntity;

    // Trigger change detection and emit changes
    this._ref.detectChanges();
    this.emitChanges();
  }

  // Method to get cell identifier for drag-drop
  getCellId(rowIndex: number, cellIndex: number): string {
    return `cell-${rowIndex}-${cellIndex}`;
  }

  // Check if cell has an entity that can be dragged
  canDragCell(cell: EntityTableCelllayout): boolean {
    return cell.type === 'entity' && cell.entity !== null;
  }

  // Check if cell can accept a drop
  canDropInCell(cell: EntityTableCelllayout): boolean {
    return cell.type === 'entity';
  }

  // Get all connected cell IDs for drag-drop connections
  getConnectedCellIds(): string[] {
    const connectedIds: string[] = [];
    if (this.layout?.rows) {
      this.layout.rows.forEach((row, rowIndex) => {
        row.cells.forEach((cell, cellIndex) => {
          if (cell.type === 'entity') {
            connectedIds.push(this.getCellId(rowIndex, cellIndex));
          }
        });
      });
    }
    return connectedIds;
  }

  // Cell-level drag and drop functionality
  dropCell(event: CdkDragDrop<EntityTableCelllayout[]>) {
    if (event.previousContainer === event.container) {
      // Same row - reorder cells within the row
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      // Different row - transfer cell between rows
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }

    // Trigger change detection and emit changes
    this._ref.detectChanges();
    this.emitChanges();
  }

  // Get row identifier for cell drag-drop
  getRowId(rowIndex: number): string {
    return `row-${rowIndex}`;
  }

  // Get all connected row IDs for cell drag-drop connections
  getConnectedRowIds(): string[] {
    const connectedIds: string[] = [];
    if (this.layout?.rows) {
      this.layout.rows.forEach((row, rowIndex) => {
        connectedIds.push(this.getRowId(rowIndex));
      });
    }
    return connectedIds;
  }

  // Check if cell can be dragged (any cell type)
  canDragEntireCell(cell: EntityTableCelllayout): boolean {
    return true; // All cells can be dragged
  }

  // Check if entity is a List Data Entity
  isListDataEntity(entity: any): boolean {
    return entity && entity.dataEntityTypeCode === 'list-data';
  }

  // Get the number of list items for a list data entity
  getListItemCount(entity: any): number {
    if (!this.isListDataEntity(entity)) return 0;

    // Check availableListItems first (populated list)
    if (entity.availableListItems && entity.availableListItems.length > 0) {
      return entity.availableListItems.length;
    }

    // Fall back to listTextValues
    if (entity.listTextValues && entity.listTextValues.length > 0) {
      return entity.listTextValues.length;
    }

    return 0;
  }

  // Handle when dynamic columns checkbox changes
  onDynamicColumnsChanged(row: EntityTableRowLayout, cell: EntityTableCelllayout) {
    if (!cell.renderListItemsAsDynamicColumns) {
      // Reset colspan when disabling dynamic columns
      cell.colspan = 1;
    }
    this.emitChanges();
  }

  // Calculate the actual column count for a row, accounting for rowspan from previous rows
  private calculateRowColumnCount(rowIndex: number): number {
    const row = this.layout.rows[rowIndex];
    let columnCount = 0;

    // Add columns from cells in this row
    row.cells.forEach(c => {
      if (c.renderListItemsAsDynamicColumns && this.isListDataEntity(c.entity)) {
        columnCount += this.getListItemCount(c.entity);
      } else {
        columnCount += (c.colspan || 1);
      }
    });

    // Add columns from cells in previous rows that span into this row
    for (let prevRowIdx = 0; prevRowIdx < rowIndex; prevRowIdx++) {
      const prevRow = this.layout.rows[prevRowIdx];
      prevRow.cells.forEach(cell => {
        const cellRowSpan = cell.rowSpan || 1;
        // Check if this cell's rowspan extends into the current row
        if (prevRowIdx + cellRowSpan > rowIndex) {
          // This cell spans into the current row
          if (cell.renderListItemsAsDynamicColumns && this.isListDataEntity(cell.entity)) {
            columnCount += this.getListItemCount(cell.entity);
          } else {
            columnCount += (cell.colspan || 1);
          }
        }
      });
    }

    return columnCount;
  }

  // Validate dynamic columns configuration
  getDynamicColumnsValidation(row: EntityTableRowLayout, cell: EntityTableCelllayout): { isValid: boolean; message: string; expectedColumns: number } | null {
    if (!cell.renderListItemsAsDynamicColumns || !this.isListDataEntity(cell.entity)) {
      return null;
    }

    const listItemCount = this.getListItemCount(cell.entity);

    if (listItemCount === 0) {
      return {
        isValid: false,
        message: 'List has no items defined. Please configure the list first.',
        expectedColumns: 0
      };
    }

    // Calculate expected total columns for each row, accounting for rowspan
    const currentRowIndex = this.layout.rows.indexOf(row);
    const expectedColumns = this.calculateRowColumnCount(currentRowIndex);

    // Check all rows for column consistency
    let inconsistentRows: number[] = [];

    this.layout.rows.forEach((r, idx) => {
      const rowColumns = this.calculateRowColumnCount(idx);

      if (rowColumns !== expectedColumns) {
        inconsistentRows.push(idx + 1); // 1-indexed for display
      }
    });

    if (inconsistentRows.length > 0) {
      return {
        isValid: false,
        message: `Column count mismatch! Row(s) ${inconsistentRows.join(', ')} have different column counts. Expected ${expectedColumns} total columns per row (accounting for rowspan).`,
        expectedColumns: listItemCount
      };
    }

    return {
      isValid: true,
      message: '',
      expectedColumns: listItemCount
    };
  }

  // Get cells with placeholders for editor display
  getCellsWithPlaceholders(rowIndex: number): Array<{ cell: EntityTableCelllayout | null, isPlaceholder: boolean, placeholderType?: 'rowspan' | 'dynamic-column', listItem?: any }> {
    const row = this.layout.rows[rowIndex];
    if (!row) return [];

    const result: Array<{ cell: EntityTableCelllayout | null, isPlaceholder: boolean, placeholderType?: 'rowspan' | 'dynamic-column', listItem?: any }> = [];

    // Build a map of which column positions are occupied by rowspan from previous rows
    const occupiedColumns = new Map<number, { cell: EntityTableCelllayout, rowIndex: number }>();

    // Check all previous rows for cells that span into this row
    for (let prevRowIdx = 0; prevRowIdx < rowIndex; prevRowIdx++) {
      const prevRow = this.layout.rows[prevRowIdx];
      let colIndex = 0;

      prevRow.cells.forEach((prevCell: EntityTableCelllayout) => {
        const rowSpan = prevCell.rowSpan || 1;

        // Calculate the actual column span (considering dynamic columns)
        let effectiveColspan = prevCell.colspan || 1;
        if (prevCell.renderListItemsAsDynamicColumns && this.isListDataEntity(prevCell.entity)) {
          effectiveColspan = this.getListItemCount(prevCell.entity);
        }

        // If this cell spans into the current row, mark its columns as occupied
        if (prevRowIdx + rowSpan > rowIndex) {
          for (let i = 0; i < effectiveColspan; i++) {
            occupiedColumns.set(colIndex + i, { cell: prevCell, rowIndex: prevRowIdx });
          }
        }

        colIndex += effectiveColspan;
      });
    }

    // Build the result array with real cells and placeholders
    let currentColIndex = 0;
    let cellArrayIndex = 0;

    // Calculate total columns needed
    const maxColumns = Math.max(...this.layout.rows.map(r => {
      let total = 0;
      r.cells.forEach(c => {
        if (c.renderListItemsAsDynamicColumns && this.isListDataEntity(c.entity)) {
          total += this.getListItemCount(c.entity);
        } else {
          total += (c.colspan || 1);
        }
      });
      return total;
    }));

    while (currentColIndex < maxColumns && (cellArrayIndex < row.cells.length || occupiedColumns.has(currentColIndex))) {
      if (occupiedColumns.has(currentColIndex)) {
        // Add placeholder for occupied column (rowspan from previous row)
        const occupiedInfo = occupiedColumns.get(currentColIndex)!;
        result.push({ cell: occupiedInfo.cell, isPlaceholder: true, placeholderType: 'rowspan' });
        currentColIndex++;
      } else if (cellArrayIndex < row.cells.length) {
        // Add actual cell
        const cell = row.cells[cellArrayIndex];

        // Check if this cell renders list items as dynamic columns
        if (cell.renderListItemsAsDynamicColumns && this.isListDataEntity(cell.entity)) {
          const entity = cell.entity as any;
          const listItems = entity.availableListItems || entity.listTextValues || [];
          const itemCount = listItems.length;

          if (itemCount > 0) {
            // Add the cell itself for the first column
            result.push({ cell: cell, isPlaceholder: false, listItem: listItems[0] });

            // Add placeholders for remaining list items
            for (let i = 1; i < itemCount; i++) {
              result.push({
                cell: cell,
                isPlaceholder: true,
                placeholderType: 'dynamic-column',
                listItem: listItems[i]
              });
            }

            currentColIndex += itemCount;
          } else {
            // No list items, just add the cell
            result.push({ cell: cell, isPlaceholder: false });
            currentColIndex += (cell.colspan || 1);
          }
        } else {
          // Normal cell without dynamic columns
          result.push({ cell: cell, isPlaceholder: false });
          const colspan = cell.colspan || 1;
          currentColIndex += colspan;
        }

        cellArrayIndex++;
      } else {
        break;
      }
    }

    return result;
  }

  // Helper method to get the actual index of a cell in the row's cells array
  getCellIndexInRow(row: EntityTableRowLayout, cell: EntityTableCelllayout): number {
    return row.cells.findIndex(c => c === cell);
  }
}
